import { Component, OnInit, Inject } from '@angular/core';
import { ErrorStateMatcher, DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormControl, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { ConfirmComponent } from '../../../shared/dialogs/confirm/confirm.component';
import { NewsService } from '../../../services/news.service';
import { News } from '../../../models/news';
import { AuthService } from '../../../services/auth.service';
import { SnackBarIconService } from '../../../shared/snack-bar-icon/snack-bar-icon.service';

import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { AngularFireStorage } from '@angular/fire/compat/storage';

/** Error when invalid control is dirty, touched, or submitted. */
export class FormErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-news-dialog',
  templateUrl: './news-dialog.component.html',
  styleUrls: ['./news-dialog.component.scss'],
  entryComponents: [
    ConfirmComponent
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' }
  ]
})
export class NewsDialogComponent implements OnInit {

  public Editor = ClassicEditor;
  public local_title: string;
  public matcher = new FormErrorStateMatcher(); // filter table
  public entityForm: FormGroup;   // store User Form
  public isEdit: boolean; // is insert or update

  constructor(
    private fs: AngularFireStorage,
    private fb: FormBuilder,
    private dialog: MatDialog,
    public _newsService: NewsService,
    private _authService: AuthService,
    private snackBar: SnackBarIconService,
    private _adapter: DateAdapter<any>,
    @Inject(MAT_DIALOG_DATA) public data: News
  ) { }


  ngOnInit() {

    this._adapter.setLocale('pt');

    // check is is insert or update
    if (this.data.id != "0") {
      this.isEdit = true;
      this.local_title = this.data.title
    } else {
      this.isEdit = false
      this.local_title = "Nova Notícia"
    }

    this.entityForm = this.createForm()
    this.setFormValues(this.data, this.entityForm)
  }

  insert() {
    if (this.entityForm.valid) {
      this._newsService.spinner = true;
      let entity: News = this.createEntity(this.entityForm.value);

      this._newsService.insert(entity)
        .then((result) => {
          this.data.id = result.id
          this.isEdit = true;
          this.entityForm.controls.id.setValue(result.id);

          if (this._newsService.objectImage) {
            this._newsService.uploadImage(this._newsService.objectImage, result.id)
          }

          if (this._newsService.objectImage_en) {
            this._newsService.uploadImage_en(this._newsService.objectImage_en, result.id)
          }

          if (!this._newsService.objectImage && !this._newsService.objectImage_en) {
            this.snackBar.openSnackBar("Sucesso!", '', 'success');
          }

        })
        .catch((error) => {
          console.log("insert newd error: " + error)
          let msgError: string = this._newsService.makeMsgError(error.details.code)
          this.snackBar.openSnackBar(msgError, '', 'error');
        })
        .finally(() => {
          this._newsService.spinner = false
        });
    }

  }

  update() {
    if (this.entityForm.valid) {

      this._newsService.spinner = true;

      let entity: News = this.createEntity(this.entityForm.value);
      let news_id = entity.id;

      this._newsService.update(entity)
        .then(() => {
          if (this._newsService.objectImage) {
            this._newsService.uploadImage(this._newsService.objectImage, news_id)
          }

          if (this._newsService.objectImage_en) {
            this._newsService.uploadImage_en(this._newsService.objectImage_en, news_id)
          }

          if (!this._newsService.objectImage && !this._newsService.objectImage_en) {
            this.snackBar.openSnackBar("Sucesso!", '', 'success');
            this._newsService.spinner = false
          }
        })
        .catch((error) => {
          console.log("news update error: " + JSON.stringify(error))
          let msgError: string = this._newsService.makeMsgError(error.details.code)
          this.snackBar.openSnackBar(msgError, '', 'error');
        })
    }
  }

  createEntity(data: any): News {
    let entity = new News();

    entity.id = data.id;

    if (data.active) {
      entity.active = true;
    } else {
      entity.active = false;
    }

    if (this.isEdit) {
      entity.created = data.created;
    } else {
      entity.created = new Date();
    }

    entity.title = data.title;
    entity.title_en = data.title_en ? data.title_en : "";

    entity.text_home = data.text_home ? data.text_home : "";
    entity.text_home_en = data.text_home_en ? data.text_home_en : "";

    entity.text = data.text ? data.text : "";
    entity.text_en = data.text_en ? data.text_en : "";

    entity.image = data.image ? data.image : "";
    entity.image_en = data.image_en ? data.image_en : "";

    return entity;
  }

  setFormValues(data, form) {

    let dtCreated
    if (data.created) {
      dtCreated = new Date(data.created.seconds * 1000)
    }
    form.controls.created.setValue(dtCreated);

    form.controls.id.setValue(data.id);
    form.controls.active.setValue(data.active);

    form.controls.image.setValue(data.image);
    form.controls.image_en.setValue(data.image_en);

    form.controls.title.setValue(data.title);
    form.controls.title_en.setValue(data.title_en);

    form.controls.text.setValue(data.text);
    form.controls.text_en.setValue(data.text_en);

    form.controls.text_home.setValue(data.text_home);
    form.controls.text_home_en.setValue(data.text_home_en);

    this._newsService.storedImage = data.image
    this._newsService.storedImage_en = data.image_en

  }

  createForm() {
    let form: FormGroup = this.fb.group({

      id: new FormControl(''),
      active: new FormControl(''),
      created: new FormControl(''),

      title: new FormControl('', [Validators.required]),
      title_en: new FormControl(''),

      text_home: new FormControl(''),
      text_home_en: new FormControl(''),

      text: new FormControl(''),
      text_en: new FormControl(''),

      image: new FormControl(''),
      image_en: new FormControl('')

    });
    return form
  }


  remove_image(type) {

    console.log("type: " + type)

    let erase_image: any = {}
    const path = this.data.id + type + ".png"

    let dialogRef = this.dialog.open(ConfirmComponent, {
      width: '350px',
      height: '280px',
      panelClass: 'custom-dialog-container',
      data: { title: "Confirme", message: "Remover esta imagem?" }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result == "ok") {

        this._newsService.spinner = true;

        switch (type) {
          case "_pt":
            erase_image = { "image": "" }
            this._newsService.storedImage = null
            this.entityForm.controls.image.setValue("");
            break;
          case "_en":
            erase_image = { "image_en": "" }
            this._newsService.storedImage_en = null
            this.entityForm.controls.image_en.setValue("");
            break;
        }

        this._newsService.remove_image(this.data.id, erase_image)
          .then((e) => {

            const newsRef = this.fs.storage.ref('images/news/' + path);

            newsRef.delete()
              .then(function () {
              }).catch(function (error) {
                console.log("remove image news error - " + path + " - " + JSON.stringify(error))
              })
              .finally(() => {
                this._newsService.spinner = false;
              });

          })
          .catch((error) => {
            console.log("remove image error: " + JSON.stringify(error))
          })
          .finally(() => {
            this._newsService.spinner = false;
          });

      }
    })
  }

}